import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import PortalDocument from "../../../document/PortalDocument";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PrintDialogFields from "../../field/document/PrintDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Environment,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxPrintPrint
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {SelectItem} from "primereact/selectitem";
import DropdownFilter from "../../../../shared/components/DropdownFilter";

interface PrintDialogStatus extends PortalDialogState<ToolboxPrintPrint> {
	pages: number;
	currentPage: string;
}

export class PrintDialogDefinition extends PortalDialog<ToolboxPrintPrint,
	PortalDialogProperties<ToolboxPrintPrint>,
	PrintDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<PrintDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.printDocument, Functions.getPrintDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];
		let printer: Array<string> = FunctionHelper.applyValue(
			portalInfo.environment?.printers, Environment.getPrintersDefault()
		);

		if (printer.length === 0) {
			return false;
		}

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.PRINT, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxPrintPrint | undefined = this.state.parameter?.data;
		let errors: Map<PrintDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let printers: Array<SelectItem> = [];
		for (let printer of FunctionHelper.applyValue(
			this.props.context.info.environment?.printers, Environment.getPrintersDefault()
		)) {
			printers.push({
				label: printer,
				value: printer
			});
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid"
						  hidden={
							  !this.state.fields?.contains(PrintDialogFields.PRINTER) &&
							  !this.state.fields?.contains(PrintDialogFields.COPIES)
						  }>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.PRINTER)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-printer">
							<Translate {...this.props} data="print.printer" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid control-printer-container">
							<DropdownFilter inputId="control-printer" options={printers}
											appendTo={document.getElementById("root")}
											value={FunctionHelper.applyValue(
												parameter?.printerName,
												(printers.length > 0 ? printers[0].value : undefined),
												ToolboxPrintPrint.getPrinterNameDefault()
											)}
											onChange={(e) => this.onChange(
												PrintDialogFields.PRINTER, e.value
											)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.COPIES)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-copies">
							<Translate {...this.props} data="print.copies" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputNumber inputId="control-copies" step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.copies, ToolboxPrintPrint.getCopiesDefault()
										 )}
										 min={ToolboxPrintPrint.getCopiesMin()}
										 onChange={(e) => this.onChange(
											 PrintDialogFields.COPIES, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={
							  !this.state.fields?.contains(PrintDialogFields.AUTO_ROTATE) &&
							  !this.state.fields?.contains(PrintDialogFields.CENTER) &&
							  !this.state.fields?.contains(PrintDialogFields.SHRINK) &&
							  !this.state.fields?.contains(PrintDialogFields.EXPAND)
						  }>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.AUTO_ROTATE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-auto-rotate"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-auto-rotate"
										 checked={FunctionHelper.applyValue(
											 parameter?.autoRotate, ToolboxPrintPrint.getAutoRotateDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PrintDialogFields.AUTO_ROTATE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-auto-rotate">
								<Translate {...this.props} data="print.auto_rotate.label" namespace="dialog"/>
								<Info {...this.props} data="print.auto_rotate.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.CENTER)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-center-in-page"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-center-in-page"
										 checked={FunctionHelper.applyValue(
											 parameter?.centerInPage, ToolboxPrintPrint.getCenterInPageDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PrintDialogFields.CENTER, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-center-in-page">
								<Translate {...this.props} data="print.center.label" namespace="dialog"/>
								<Info {...this.props} data="print.center.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.SHRINK)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-shrink-to-margins"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-shrink-to-margins"
										 checked={FunctionHelper.applyValue(
											 parameter?.shrinkToMargins, ToolboxPrintPrint.getShrinkToMarginsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PrintDialogFields.SHRINK, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-shrink-to-margins">
								<Translate {...this.props} data="print.shrink.label" namespace="dialog"/>
								<Info {...this.props} data="print.shrink.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(PrintDialogFields.EXPAND)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-expand-to-margins"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-expand-to-margins"
										 checked={FunctionHelper.applyValue(
											 parameter?.expandToMargins, ToolboxPrintPrint.getExpandToMarginsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PrintDialogFields.EXPAND, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-expand-to-margins">
								<Translate {...this.props} data="print.expand.label" namespace="dialog"/>
								<Info {...this.props} data="print.expand.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(PrintDialogFields.PAGERANGE)
				}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="print.pages.label" namespace="dialog"/>
							<Info {...this.props} data="print.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxPrintPrint.getPagesDefault()
									   )}
									   className={errors.has(PrintDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   onChange={(e) => this.onChange(
										   PrintDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(PrintDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(PrintDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(PrintDialogFields.PAGERANGE) ? Translate.value(
								errors.get(PrintDialogFields.PAGERANGE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["print-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.print.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.print.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PrintDialogFields, value: any): void {
		let parameter: ToolboxPrintPrint = this.state.parameter!.data;
		let errors: Map<PrintDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case PrintDialogFields.PRINTER:
				parameter.printerName = value;
				break;
			case PrintDialogFields.COPIES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxPrintPrint.getCopiesDefault();
				}

				parameter.copies = value;
				break;
			case PrintDialogFields.AUTO_ROTATE:
				parameter.autoRotate = value;
				break;
			case PrintDialogFields.CENTER:
				parameter.centerInPage = value;
				break;
			case PrintDialogFields.SHRINK:
				parameter.shrinkToMargins = value;
				break;
			case PrintDialogFields.EXPAND:
				parameter.expandToMargins = value;
				break;
			case PrintDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-print small"}/>
					<Translate {...this.props} data="print.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canPrint, Security.getCanPrintDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxPrintPrint | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let pagerange: string | undefined = portalDocument.metadata?.information?.viewerPreferences?.printPageRange;
		if (!FunctionHelper.isEmpty(pagerange)) {
			parameter!.pages = pagerange;
		} else {
			parameter!.pages = documentSettings.pageSelection.toString(
				pageCount, documentSettings.activeViewer.currentPage
			);
		}

		let copies: number | undefined = portalDocument.metadata?.information?.viewerPreferences?.numCopies;
		if (FunctionHelper.exists(copies)) {
			parameter!.copies = copies;
		}

		if (FunctionHelper.isEmpty(parameter?.printerName)) {
			let printer: Array<string> = FunctionHelper.applyValue(
				this.props.context.info.environment?.printers, Environment.getPrintersDefault()
			);
			parameter!.printerName = printer[0];
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			print: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.print", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PRINT;
	}

	protected getDefaultParameter(): Parameter<ToolboxPrintPrint> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const PrintDialog = React.memo(PrintDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxPrintPrint>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxPrintPrint>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PrintDialog;